import { useState, type ClipboardEvent, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../lib/format";
import { Markdown } from "./Markdown";

/**
 * Markdown textarea with a Write / Preview toggle. Optionally accepts files
 * via paste and drag-and-drop (handled by the parent through onFiles).
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  onFiles,
  onSubmitShortcut,
  autoFocus,
  footer,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  onFiles?: (files: File[]) => void;
  onSubmitShortcut?: () => void;
  autoFocus?: boolean;
  footer?: ReactNode;
  id?: string;
}) {
  const [preview, setPreview] = useState(false);
  const [dragging, setDragging] = useState(false);

  const onPaste = (e: ClipboardEvent) => {
    if (!onFiles) return;
    const files = Array.from(e.clipboardData.files);
    if (files.length) {
      e.preventDefault();
      onFiles(files);
    }
  };
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
  const onDragOver = (e: DragEvent) => {
    if (!onFiles || !hasFiles(e)) return;
    e.preventDefault();
    setDragging(true);
  };
  const onDrop = (e: DragEvent) => {
    if (!onFiles) return;
    setDragging(false);
    if (e.dataTransfer.files.length) {
      e.preventDefault();
      onFiles(Array.from(e.dataTransfer.files));
    }
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (onSubmitShortcut && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmitShortcut();
    }
  };

  return (
    <div
      className={cx("md-editor", dragging && "md-editor-dragging")}
      onDragOver={onDragOver}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <div className="md-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!preview}
          className={cx("md-tab", !preview && "active")}
          onClick={() => setPreview(false)}
        >
          Write
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={preview}
          className={cx("md-tab", preview && "active")}
          onClick={() => setPreview(true)}
        >
          Preview
        </button>
        <span className="md-hint muted">Markdown supported</span>
      </div>
      {preview ? (
        <div className="md-preview" style={{ minHeight: rows * 21 }}>
          {value.trim() ? <Markdown>{value}</Markdown> : <p className="muted">Nothing to preview</p>}
        </div>
      ) : (
        <textarea
          id={id}
          className="md-textarea"
          rows={rows}
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
        />
      )}
      {dragging && <div className="md-drop-hint">Drop files to attach</div>}
      {footer}
    </div>
  );
}
